import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { makeObservable } from 'mobx';
import { injectable } from '@servicetitan/react-ioc';
import AuthModule from '@modules/AuthModule';
import { logger } from '../utils/utils';

@injectable()
export class AxiosStore {
  private readonly instance: AxiosInstance;

  constructor() {
    makeObservable(this);

    const tenantId = '123';
    const LOCALHOST_PATH = 'contactcenter';
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(
      window.location.hostname,
    );
    const path = isLocalhost ? LOCALHOST_PATH : window.location.hostname;

    this.instance = axios.create({
      headers: {
        // Temporary ST-Tenant-Id header until its made optional in the AIS API
        'ST-Tenant-Id': tenantId,
        'X-Current-Path': path,
      },
    });

    this.registerInterceptors();
  }

  get axiosInstance(): AxiosInstance {
    return this.instance;
  }

  private registerInterceptors(): void {
    // Request interceptor to add the authorization token
    this.instance.interceptors.request.use(async (config) => {
      const token = (await AuthModule.getCurrentToken()) ?? '';

      config.headers['Authorization'] = `Bearer ${token}`;

      return config;
    });

    // Response interceptor to handle errors
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        this.handleResponseError(error);
        return Promise.reject(error);
      },
    );
  }

  private handleResponseError(error: AxiosError): void {
    const { config, response } = error;

    const errorContext = {
      method: config?.method?.toUpperCase(),
      url: config?.url,
      baseURL: config?.baseURL,
      status: response?.status,
      statusText: response?.statusText,
      data: response?.data,
      timestamp: new Date().toISOString(),
      tenantId: config?.headers?.['ST-Tenant-Id'],
      currentPath: config?.headers?.['X-Current-Path'],
    };

    if (response?.status) {
      if (response.status >= 500) {
        logger.error('Server Error (5xx):', errorContext);
      } else if (response.status >= 400) {
        logger.warn('Client Error (4xx):', errorContext);
      }
    } else {
      logger.error('Network/Request Error:', errorContext);
    }
  }
}
